import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ClipboardList, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const MONTH_NAMES = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const DAY_NAMES = ['Mo','Di','Mi','Do','Fr','Sa','So'];

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month - 1, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

export default function CalendarPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/calendar/events?year=${year}&month=${month}`)
      .then(res => setEvents(res.data.events || []))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [year, month]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y-1); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y+1); } else setMonth(m => m+1); };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const selectedDayEvents = selected ? getEventsForDay(selected) : [];

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="calendar-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl">Kalender</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Termine und Deadlines im Überblick</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevMonth} className="rounded-sm">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-heading font-bold text-lg px-2 min-w-[160px] text-center">
              {MONTH_NAMES[month-1]} {year}
            </span>
            <Button variant="outline" size="sm" onClick={nextMonth} className="rounded-sm">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-3 bg-card border border-border/60 rounded-sm overflow-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-border/60">
              {DAY_NAMES.map(d => (
                <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {d}
                </div>
              ))}
            </div>
            {/* Days */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-7">
                {/* Empty cells */}
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`empty-${i}`} className="min-h-[80px] border-b border-r border-border/40 bg-muted/20" />
                ))}
                {/* Day cells */}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const isToday = dateStr === todayStr;
                  const dayEvents = getEventsForDay(day);
                  const isSelected = selected === day;
                  return (
                    <div
                      key={day}
                      onClick={() => setSelected(isSelected ? null : day)}
                      className={cn(
                        "min-h-[80px] border-b border-r border-border/40 p-1.5 cursor-pointer transition-colors",
                        isSelected ? "bg-primary/10" : "hover:bg-accent",
                        (firstDay + i) % 7 === 6 && "border-r-0"
                      )}
                    >
                      <div className={cn(
                        "w-6 h-6 flex items-center justify-center text-sm font-medium rounded-full mb-1",
                        isToday ? "bg-primary text-primary-foreground" : "text-foreground"
                      )}>
                        {day}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map((ev, idx) => (
                          <div key={idx} className={cn(
                            "text-xs px-1 py-0.5 rounded-sm truncate",
                            ev.type === 'order' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          )}>
                            {ev.number}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-muted-foreground pl-1">+{dayEvents.length - 3}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Side panel */}
          <div className="space-y-4">
            {/* Legend */}
            <div className="bg-card border border-border/60 rounded-sm p-4">
              <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">Legende</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-sm bg-blue-200" />
                  <span className="text-muted-foreground">Auftrag</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-sm bg-amber-200" />
                  <span className="text-muted-foreground">Offerte</span>
                </div>
              </div>
            </div>

            {/* Selected day events */}
            <div className="bg-card border border-border/60 rounded-sm p-4">
              <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                {selected ? `${selected}. ${MONTH_NAMES[month-1]}` : 'Tag auswählen'}
              </h3>
              {!selected && <p className="text-xs text-muted-foreground">Klicken Sie auf einen Tag</p>}
              {selected && selectedDayEvents.length === 0 && (
                <p className="text-xs text-muted-foreground">Keine Termine</p>
              )}
              <div className="space-y-2">
                {selectedDayEvents.map((ev, idx) => (
                  <Link
                    key={idx}
                    to={ev.type === 'order' ? `/orders/${ev.id}` : `/quotes/${ev.id}`}
                    className="block p-2.5 bg-muted rounded-sm hover:bg-accent transition-colors"
                    data-testid={`calendar-event-${ev.id}`}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {ev.type === 'order'
                        ? <ClipboardList className="w-3 h-3 text-blue-500 shrink-0" strokeWidth={1.5} />
                        : <FileText className="w-3 h-3 text-amber-500 shrink-0" strokeWidth={1.5} />}
                      <span className="text-xs font-mono text-muted-foreground">{ev.number}</span>
                    </div>
                    <p className="text-xs font-medium text-foreground line-clamp-2">{ev.title}</p>
                    {ev.customer_name && <p className="text-xs text-muted-foreground mt-0.5">{ev.customer_name}</p>}
                  </Link>
                ))}
              </div>
            </div>

            {/* Month summary */}
            <div className="bg-card border border-border/60 rounded-sm p-4">
              <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-2">Monat</h3>
              <p className="text-2xl font-heading font-black text-foreground">{events.length}</p>
              <p className="text-xs text-muted-foreground">Termine total</p>
              <div className="mt-2 flex gap-3 text-xs text-muted-foreground">
                <span>{events.filter(e => e.type === 'order').length} Aufträge</span>
                <span>{events.filter(e => e.type === 'quote').length} Offerten</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
